// Intent: the app-side of the pol_transfers mobile AUTH contract
// (/api/public/mobile/auth/*, built 2026-07-10). Includes a MOCK transport so the
// login flow can be built and E2E-tested on the web preview BEFORE the backend is
// reachable (dev DB needs the 1.52 SQL first). Mock activates only when
// EXPO_PUBLIC_API_URL is unset; it mimics the real responses, accepts code 123456
// and enforces the same 30 s resend cooldown, so swapping to the real server is
// a config change, not a code change.

import { API_URL, postJson } from '@/api/client';

export type MatchStatus = 'NONE' | 'SINGLE_MASKED' | 'MULTIPLE' | 'LINKED';

/** Mirror of MobileAccountDto (backend). */
export interface MobileAccount {
  id: number;
  /** Null for accounts created via e-mail/Google before attaching a phone (1.2.0). */
  phone: string | null;
  /** Null for SMS-era accounts; login identifier of the e-mail/Google paths. */
  email?: string | null;
  firstName: string | null;
  linkStatus: string;
}

/** Mirror of MobileMaskedMatchDto — masked "Czy to Ty?" preview, nothing more. */
export interface MaskedMatch {
  name: string;
  policiesCount: number;
}

/** Mirror of MobileAuthResponseDto. */
export interface AuthPayload {
  accessToken: string;
  refreshToken: string;
  account: MobileAccount;
  matchStatus: MatchStatus;
  maskedMatch: MaskedMatch | null;
  /**
   * Presentation-only: the account's phone matches a CRM user who is an agent,
   * so the app shows the AGENT tab. Every agent-mode endpoint re-checks
   * server-side — this flag never grants anything by itself.
   */
  isAgent?: boolean;
  /**
   * Presentation-only: the account's phone matches a CRM ADMIN — the AGENT tab
   * switches to admin view (ALL chats: both websites + app). Server re-checks
   * on every admin endpoint; this flag never grants anything by itself.
   */
  isAdmin?: boolean;
  /**
   * 1.2.0: this session logged in without SMS (e-mail/Google) while the account
   * HAS a phone — the app must run the SMS 2FA step before pulling cloud data
   * (the server enforces it on the sync pull anyway).
   */
  requiresSmsVerify?: boolean;
}

export interface AuthResult<T = undefined> {
  ok: boolean;
  message: string | null;
  data?: T;
}

export async function requestOtp(phone: string, deviceId: string): Promise<AuthResult> {
  if (!API_URL) return mockRequestOtp(phone);
  const res = await postJson('/api/public/mobile/auth/otp/request', { phone, deviceId });
  return { ok: res.status, message: res.message };
}

export async function verifyOtp(
  phone: string,
  code: string,
  deviceId: string,
  firstName?: string,
  referralCode?: string,
): Promise<AuthResult<AuthPayload>> {
  if (!API_URL) return mockVerifyOtp(phone, code, firstName);
  const res = await postJson<AuthPayload>('/api/public/mobile/auth/otp/verify', {
    phone,
    code,
    deviceId,
    firstName,
    // A friend's referral code typed on the login screen (50 zł voucher program);
    // the backend uses it only when this login CREATES the account.
    referralCode,
  });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function refreshSession(refreshToken: string): Promise<AuthResult<AuthPayload>> {
  if (!API_URL) return mockRefresh(refreshToken);
  const res = await postJson<AuthPayload>('/api/public/mobile/auth/refresh', { refreshToken });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ------------------------------------------------- e-mail (magic link) login
// "Confirm and return" pattern (1.2.0): the backend mails a link from
// opiekun@polfinance.pl, the app polls with the challengeId until the user
// clicks it (or types the 6-digit fallback code) — no deep links needed.

export type EmailPollStatus = 'PENDING' | 'OK' | 'EXPIRED';

export interface EmailPollResult {
  status: EmailPollStatus;
  auth: AuthPayload | null;
}

export async function requestEmailLogin(
  email: string,
  deviceId: string,
): Promise<AuthResult<{ challengeId: string }>> {
  if (!API_URL) return mockRequestEmailLogin(email);
  const res = await postJson<{ challengeId: string }>('/api/public/mobile/auth/email/request', {
    email,
    deviceId,
  });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function pollEmailLogin(
  challengeId: string,
  deviceId: string,
): Promise<AuthResult<EmailPollResult>> {
  if (!API_URL) return mockPollEmailLogin(challengeId);
  try {
    const res = await fetch(
      `${API_URL}/api/public/mobile/auth/email/poll?challengeId=${encodeURIComponent(challengeId)}&deviceId=${encodeURIComponent(deviceId)}`,
    );
    const body = (await res.json()) as { status: boolean; message: string | null; data?: EmailPollResult };
    return { ok: body.status, message: body.message, data: body.data ?? undefined };
  } catch {
    // Network hiccup mid-poll: report PENDING so the screen just keeps waiting.
    return { ok: true, message: null, data: { status: 'PENDING', auth: null } };
  }
}

export async function verifyEmailCode(
  challengeId: string,
  code: string,
  deviceId: string,
  firstName?: string,
  referralCode?: string,
): Promise<AuthResult<AuthPayload>> {
  if (!API_URL) return mockVerifyEmailCode(challengeId, code, firstName);
  const res = await postJson<AuthPayload>('/api/public/mobile/auth/email/verify-code', {
    challengeId,
    code,
    deviceId,
    firstName,
    referralCode,
  });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ---------------------------------------------------------- Google Sign-In

export async function googleLogin(
  idToken: string,
  deviceId: string,
  firstName?: string,
  referralCode?: string,
): Promise<AuthResult<AuthPayload>> {
  if (!API_URL) return mockGoogleLogin(firstName);
  const res = await postJson<AuthPayload>('/api/public/mobile/auth/google', {
    idToken,
    deviceId,
    firstName,
    referralCode,
  });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ---------------------------------------------------------------- mock transport

const MOCK_CODE = '123456';
const mockLastRequestAt = new Map<string, number>();

function mockRequestOtp(phone: string): AuthResult {
  const last = mockLastRequestAt.get(phone) ?? 0;
  if (Date.now() - last < 30_000) {
    return { ok: false, message: 'Poczekaj 30 sekund przed ponowną wysyłką kodu.' };
  }
  mockLastRequestAt.set(phone, Date.now());
  console.warn(`[mock auth] OTP for ${phone}: ${MOCK_CODE}`);
  return { ok: true, message: 'Kod wysłany SMS-em.' };
}

function mockVerifyOtp(phone: string, code: string, firstName?: string): AuthResult<AuthPayload> {
  if (code !== MOCK_CODE) {
    return { ok: false, message: 'Nieprawidłowy kod.' };
  }
  // Phone suffix steers the auto-match scenario so every backend branch is
  // testable in the preview: ...111 => single masked customer, ...222 => multiple,
  // ...999 => the account is an AGENT (agent-mode tab shows up).
  const matchStatus: MatchStatus = phone.endsWith('111')
    ? 'SINGLE_MASKED'
    : phone.endsWith('222')
      ? 'MULTIPLE'
      : 'NONE';
  return {
    ok: true,
    message: 'Zalogowano.',
    data: {
      accessToken: `mock-access-${Date.now()}`,
      refreshToken: `mock-refresh-${Math.random().toString(36).slice(2)}`,
      account: { id: 1, phone, firstName: firstName ?? null, linkStatus: 'NONE' },
      matchStatus,
      maskedMatch:
        matchStatus === 'SINGLE_MASKED' ? { name: 'Jan K.', policiesCount: 2 } : null,
      isAgent: phone.endsWith('999'),
    },
  };
}

// E-mail login mock: the "click in the mail" auto-happens ~4 s after request,
// so the email-sent screen's polling can be E2E-tested on the web preview.
// Code 123456 works as the manual fallback, same as the SMS mock.
const mockEmailChallenges = new Map<string, { email: string; verifiedAt: number }>();

function mockRequestEmailLogin(email: string): AuthResult<{ challengeId: string }> {
  const last = mockLastRequestAt.get(`mail:${email}`) ?? 0;
  if (Date.now() - last < 30_000) {
    return { ok: false, message: 'Poczekaj 30 sekund przed ponowną wysyłką maila.' };
  }
  mockLastRequestAt.set(`mail:${email}`, Date.now());
  const challengeId = `mock-challenge-${Math.random().toString(36).slice(2)}`;
  mockEmailChallenges.set(challengeId, { email, verifiedAt: Date.now() + 4_000 });
  console.warn(`[mock auth] e-mail login for ${email}: auto-verifies in 4 s, code ${MOCK_CODE}`);
  return { ok: true, message: 'Mail z linkiem wysłany.', data: { challengeId } };
}

function mockEmailAuthPayload(email: string, firstName?: string): AuthPayload {
  return {
    accessToken: `mock-access-${Date.now()}`,
    refreshToken: `mock-refresh-${Math.random().toString(36).slice(2)}`,
    account: { id: 2, phone: null, email, firstName: firstName ?? null, linkStatus: 'NONE' },
    matchStatus: 'NONE',
    maskedMatch: null,
    isAgent: false,
    // Suffix "+2fa" in the mailbox part simulates an account that already has a
    // phone → the app must show the SMS 2FA step (restore path E2E on web).
    requiresSmsVerify: email.split('@')[0].endsWith('+2fa'),
  };
}

function mockPollEmailLogin(challengeId: string): AuthResult<EmailPollResult> {
  const challenge = mockEmailChallenges.get(challengeId);
  if (!challenge) {
    return { ok: false, message: 'Logowanie wygasło. Zacznij od nowa.' };
  }
  if (Date.now() < challenge.verifiedAt) {
    return { ok: true, message: null, data: { status: 'PENDING', auth: null } };
  }
  mockEmailChallenges.delete(challengeId);
  return {
    ok: true,
    message: 'Zalogowano.',
    data: { status: 'OK', auth: mockEmailAuthPayload(challenge.email) },
  };
}

function mockVerifyEmailCode(
  challengeId: string,
  code: string,
  firstName?: string,
): AuthResult<AuthPayload> {
  const challenge = mockEmailChallenges.get(challengeId);
  if (!challenge) {
    return { ok: false, message: 'Logowanie wygasło. Zacznij od nowa.' };
  }
  if (code !== MOCK_CODE) {
    return { ok: false, message: 'Nieprawidłowy kod.' };
  }
  mockEmailChallenges.delete(challengeId);
  return { ok: true, message: 'Zalogowano.', data: mockEmailAuthPayload(challenge.email, firstName) };
}

function mockGoogleLogin(firstName?: string): AuthResult<AuthPayload> {
  return {
    ok: true,
    message: 'Zalogowano.',
    data: mockEmailAuthPayload('demo.google@gmail.com', firstName ?? 'Demo'),
  };
}

function mockRefresh(refreshToken: string): AuthResult<AuthPayload> {
  if (!refreshToken.startsWith('mock-refresh-')) {
    return { ok: false, message: 'Sesja wygasła. Zaloguj się ponownie.' };
  }
  return {
    ok: true,
    message: null,
    data: {
      accessToken: `mock-access-${Date.now()}`,
      refreshToken: `mock-refresh-${Math.random().toString(36).slice(2)}`,
      account: { id: 1, phone: '+48500600700', firstName: null, linkStatus: 'NONE' },
      matchStatus: 'NONE',
      maskedMatch: null,
    },
  };
}
